/**
 * PostPreviewModal — "Ver aplicado" / "Vista previa publicada".
 *
 * Fase 3 of the Copy Studio: opens on top of the Copy chat when the user
 * clicks the green eye button on an assistant card. Renders the copy as
 * it would appear on Instagram / Facebook / LinkedIn, supports image
 * carousel, lets the user pull images from the latest generation,
 * upload local files, download everything as a .zip, and approve the
 * copy.
 *
 * The platform tabs use deliberately approximate look-alikes (avatars,
 * action bars, fonts) so the experience reads as the right network
 * without trying to be pixel-perfect — the user explicitly accepted a
 * "decent enough" rendering.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Download, Upload, Image as ImageIcon, Check, Copy as CopyIcon, ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import { api, fileUrl, thumbUrl } from "../lib/api";
import ReferencePicker from "./ReferencePicker";

const PLATFORMS = [
    { id: "instagram", label: "Instagram" },
    { id: "facebook", label: "Facebook" },
    { id: "linkedin", label: "LinkedIn" },
];

/**
 * Persistence: each copy message stores its preview-strip images on the
 * server (`PUT /copy/messages/{id}/preview-images`). On mount we hydrate
 * from `GET ...preview-images`, and any change to the strip flushes to
 * the backend (debounced). Local uploads go through a dedicated upload
 * endpoint so they survive a hard refresh — the legacy in-memory
 * `URL.createObjectURL` flow is gone.
 */

export default function PostPreviewModal({ open, onClose, message, initialPlatform = "instagram" }) {
    const [platform, setPlatform] = useState(initialPlatform);
    const [images, setImages] = useState([]); // {storage_path, url, thumb, name, source}[]
    const [activeIdx, setActiveIdx] = useState(0);
    const [approving, setApproving] = useState(false);
    const [uploading, setUploading] = useState(false);
    // Index of the thumb currently being dragged + the index it's
    // hovering over. We track them separately so we can highlight the
    // drop target without committing the reorder until the user
    // releases the mouse (avoids janky reorders on `dragenter`).
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);
    // ReferencePicker overlay — opens on "Traer desde referencias".
    const [refPickerOpen, setRefPickerOpen] = useState(false);
    const fileRef = useRef(null);
    // Used to suppress the save-effect during the very first hydration
    // (otherwise we'd PUT an empty list right after loading data).
    const hydratedRef = useRef(false);
    const saveTimerRef = useRef(null);

    // ESC key + click-outside close. Tied to `open` so we don't bind
    // global listeners when the modal isn't visible.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Hydrate from the backend when the modal opens for a given message.
    // This makes the strip survive close/reopen, page refresh, AND
    // navigating to another chat and back.
    useEffect(() => {
        if (!open || !message?.id) return;
        hydratedRef.current = false;
        setPlatform(initialPlatform);
        setActiveIdx(0);
        api.get(`/copy/messages/${message.id}/preview-images`)
            .then((r) => {
                const items = (r.data?.images || []).map((it) => ({
                    storage_path: it.storage_path,
                    url: fileUrl(it.storage_path),
                    thumb: thumbUrl(it.storage_path, 960),
                    name: it.name || "image.png",
                    source: it.source || "reference",
                }));
                setImages(items);
            })
            .catch(() => setImages([]))
            .finally(() => {
                hydratedRef.current = true;
            });
    }, [open, initialPlatform, message?.id]);

    // Persist the current strip to the backend whenever it changes
    // (debounced 400 ms so rapid add/remove operations coalesce into a
    // single PUT). Skipped while hydration is still pending.
    useEffect(() => {
        if (!open || !message?.id || !hydratedRef.current) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            const payload = {
                images: images
                    .filter((im) => im.storage_path)
                    .map((im) => ({
                        storage_path: im.storage_path,
                        name: im.name,
                        source: im.source,
                    })),
            };
            api.put(`/copy/messages/${message.id}/preview-images`, payload).catch(
                () => {
                    // Silent — the strip stays in local state regardless,
                    // and the next change will retry.
                },
            );
        }, 400);
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, [images, open, message?.id]);

    /* --- pull images from references (any tab — marca, mis refs,
     *      externas, biblioteca, carruseles). The user picks one or
     *      many; if they pick a carousel, ReferencePicker already
     *      flattens it into individual slides (sorted), so we just
     *      append them to the strip in order. --- */
    const onRefPicked = (items) => {
        setRefPickerOpen(false);
        if (!items || items.length === 0) return;
        const before = images.length;
        const picked = items.map((it) => ({
            storage_path: it.storage_path,
            url: fileUrl(it.storage_path),
            thumb: thumbUrl(it.storage_path, 960),
            name: (it.title || it.file_id) + ".png",
            source: "reference",
        }));
        setImages((prev) => [...prev, ...picked]);
        setActiveIdx(before);
        toast.success(`+${picked.length} ${picked.length === 1 ? "imagen" : "imágenes"}`);
    };

    /* --- local upload — sends each file to the backend so it persists
     *     across refresh / re-open. We previously used object-URLs
     *     which evaporated when the tab closed. --- */
    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0 || !message?.id) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const r = await api.post(
                    `/copy/messages/${message.id}/preview-upload`,
                    fd,
                );
                uploaded.push({
                    storage_path: r.data.storage_path,
                    url: fileUrl(r.data.storage_path),
                    thumb: thumbUrl(r.data.storage_path, 960),
                    name: r.data.name,
                    source: "upload",
                });
            }
            setImages((prev) => [...prev, ...uploaded]);
            toast.success(
                `+${uploaded.length} ${uploaded.length === 1 ? "imagen subida" : "imágenes subidas"}`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo subir la imagen");
        } finally {
            setUploading(false);
        }
    };

    /* --- remove image from strip --- */
    const removeImage = (idx) => {
        setImages((prev) => {
            const next = prev.filter((_, i) => i !== idx);
            return next;
        });
        setActiveIdx((i) => Math.max(0, Math.min(i, images.length - 2)));
    };

    /* --- ZIP download --- */
    const downloadZip = async () => {
        if (images.length === 0) {
            toast.error("Añade al menos una imagen");
            return;
        }
        try {
            const JSZip = (await import("jszip")).default;
            const zip = new JSZip();
            // Add copy text as a .txt file so it travels alongside the
            // images.
            zip.file("copy.txt", message?.text || "");
            // Fetch each image and add to zip. We do this in parallel
            // (Promise.all) — slowest image determines total time.
            await Promise.all(
                images.map(async (im, i) => {
                    const blob = await fetch(im.url).then((r) => r.blob());
                    const ext = (im.name?.split(".").pop() || "png").toLowerCase();
                    zip.file(`imagen-${i + 1}.${ext}`, blob);
                }),
            );
            const out = await zip.generateAsync({ type: "blob" });
            const url = URL.createObjectURL(out);
            const a = document.createElement("a");
            a.href = url;
            a.download = `post-${platform}-${Date.now()}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("ZIP descargado");
        } catch (e) {
            toast.error("No se pudo crear el ZIP");
        }
    };

    /* --- approve copy --- */
    const approve = async () => {
        if (!message?.id) return;
        setApproving(true);
        try {
            await api.post(`/copy/messages/${message.id}/approve`);
            toast.success("Copy aprobado");
            onClose?.();
        } catch {
            toast.error("No se pudo aprobar");
        } finally {
            setApproving(false);
        }
    };

    const copyText = () => {
        navigator.clipboard.writeText(message?.text || "");
        toast.success("Copiado");
    };

    if (!open) return null;

    /* --- reorder via native HTML5 drag and drop ---
     * On drop we splice `from` out and re-insert at `to`. We also
     * remap `activeIdx` so the previously-selected thumb stays
     * selected after the reorder (the carousel preview stays stable).
     * The persistence effect picks up the new order automatically
     * and PUTs it within 400 ms.
     */
    const reorderImages = (from, to) => {
        if (from === to || from == null || to == null) return;
        setImages((prev) => {
            const next = prev.slice();
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
        setActiveIdx((cur) => {
            if (cur === from) return to;
            // Adjust the active index when other thumbs cross over it.
            if (from < cur && to >= cur) return cur - 1;
            if (from > cur && to <= cur) return cur + 1;
            return cur;
        });
    };

    const handleDragStart = (i) => (e) => {
        setDragIdx(i);
        e.dataTransfer.effectAllowed = "move";
        // Firefox requires data to be set or the drag is canceled.
        try {
            e.dataTransfer.setData("text/plain", String(i));
        } catch {
            // some browsers throw on setData for non-text drags — ignore
        }
    };
    const handleDragOver = (i) => (e) => {
        e.preventDefault(); // required to allow drop
        e.dataTransfer.dropEffect = "move";
        if (overIdx !== i) setOverIdx(i);
    };
    const handleDrop = (i) => (e) => {
        e.preventDefault();
        reorderImages(dragIdx, i);
        setDragIdx(null);
        setOverIdx(null);
    };
    const handleDragEnd = () => {
        setDragIdx(null);
        setOverIdx(null);
    };

    const activeImage = images[activeIdx];

    return (
        <div
            data-testid="post-preview-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/85 backdrop-blur-sm p-4"
            onMouseDown={(e) => {
                // Click on the backdrop closes; clicks inside the panel
                // shouldn't bubble.
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <div className="w-full max-w-5xl max-h-[92vh] rounded-2xl border hairline border-ink-600 bg-ink-900 flex flex-col overflow-hidden">
                {/* header */}
                <div className="px-6 py-4 border-b hairline border-ink-700 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-base font-display tracking-tight text-fog-100">
                            Vista previa publicada
                        </h2>
                        <p className="font-mono text-[10px] text-fog-500 tracking-widest uppercase">
                            Cómo se verá tu copy en cada red
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-xl bg-ink-800 border hairline border-ink-600 p-1">
                            {PLATFORMS.map((p) => (
                                <button
                                    key={p.id}
                                    data-testid={`preview-platform-${p.id}`}
                                    onClick={() => setPlatform(p.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                        platform === p.id
                                            ? "bg-periwinkle-500 text-ink-on"
                                            : "text-fog-400 hover:text-fog-100"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="preview-close"
                            onClick={onClose}
                            className="w-9 h-9 rounded-lg border hairline border-ink-600 text-fog-300 hover:text-red-400 hover:border-red-500/50 flex items-center justify-center"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* image action bar */}
                <div className="px-6 py-3 border-b hairline border-ink-700 flex items-center justify-between shrink-0 bg-ink-900/60">
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            data-testid="preview-fetch-references"
                            onClick={() => setRefPickerOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-200 hover:text-periwinkle-800 hover:border-periwinkle-500/50 text-xs"
                        >
                            <FolderOpen className="w-3.5 h-3.5" />
                            Traer desde referencias
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={onUpload}
                            className="hidden"
                            data-testid="preview-file-input"
                        />
                        <button
                            data-testid="preview-upload-btn"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-200 hover:text-periwinkle-800 hover:border-periwinkle-500/50 text-xs disabled:opacity-50"
                        >
                            <Upload className="w-3.5 h-3.5" />
                            {uploading ? "Subiendo…" : "Subir"}
                        </button>
                        <button
                            data-testid="preview-zip-btn"
                            onClick={downloadZip}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hairline border-ink-600 text-fog-200 hover:text-emerald-400 hover:border-emerald-500/50 text-xs"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Descargar ZIP
                        </button>
                    </div>
                    <span className="font-mono text-[10px] text-fog-500 uppercase tracking-widest">
                        {images.length} {images.length === 1 ? "imagen" : "imágenes"}
                    </span>
                </div>

                {/* preview body */}
                <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-ink-900 to-ink-950">
                    <PlatformPost
                        platform={platform}
                        text={message?.text || ""}
                        images={images}
                        activeIdx={activeIdx}
                        onPrev={() => setActiveIdx((i) => Math.max(0, i - 1))}
                        onNext={() =>
                            setActiveIdx((i) =>
                                Math.min(images.length - 1, i + 1),
                            )
                        }
                    />

                    {/* thumb strip — draggable to reorder */}
                    {images.length > 0 && (
                        <div
                            className="mt-6 flex gap-2 justify-center flex-wrap"
                            data-testid="preview-thumb-strip"
                        >
                            {images.map((im, i) => {
                                const isActive = i === activeIdx;
                                const isDragging = dragIdx === i;
                                const isDropTarget =
                                    overIdx === i && dragIdx !== null && dragIdx !== i;
                                return (
                                    <div
                                        key={`${im.storage_path || i}-${i}`}
                                        draggable
                                        onDragStart={handleDragStart(i)}
                                        onDragOver={handleDragOver(i)}
                                        onDrop={handleDrop(i)}
                                        onDragEnd={handleDragEnd}
                                        className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing select-none transition-all ${
                                            isActive
                                                ? "border-periwinkle-400"
                                                : isDropTarget
                                                  ? "border-emerald-400 scale-110"
                                                  : "border-ink-700 opacity-60 hover:opacity-100"
                                        } ${isDragging ? "opacity-30" : ""}`}
                                        onClick={() => setActiveIdx(i)}
                                        data-testid={`preview-thumb-${i}`}
                                        title="Arrastra para reordenar"
                                    >
                                        <img
                                            src={im.thumb}
                                            alt={`miniatura ${i + 1}`}
                                            className="w-full h-full object-cover pointer-events-none"
                                            draggable={false}
                                        />
                                        {/* position indicator — helps the user track
                                          * where each thumb lands during reorder */}
                                        <span className="absolute bottom-0.5 left-0.5 px-1 rounded bg-ink-950/80 text-fog-100 text-[9px] font-mono pointer-events-none">
                                            {i + 1}
                                        </span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeImage(i);
                                            }}
                                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md"
                                            data-testid={`preview-thumb-remove-${i}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* footer */}
                <div className="px-6 py-4 border-t hairline border-ink-700 flex items-center justify-between shrink-0 bg-ink-900/60">
                    <button
                        data-testid="preview-copy-btn"
                        onClick={copyText}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border hairline border-ink-600 text-fog-200 hover:text-periwinkle-800 hover:border-periwinkle-500/50 text-sm"
                    >
                        <CopyIcon className="w-4 h-4" />
                        Copiar copy
                    </button>
                    <button
                        data-testid="preview-approve-btn"
                        onClick={approve}
                        disabled={approving}
                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-ink-on font-medium text-sm disabled:opacity-40"
                    >
                        <Check className="w-4 h-4" />
                        {approving ? "Aprobando…" : "Aprobar copy"}
                    </button>
                </div>
            </div>
            {refPickerOpen && (
                <ReferencePicker
                    onClose={() => setRefPickerOpen(false)}
                    onPick={onRefPicked}
                    zClass="z-[60]"
                />
            )}
        </div>
    );
}

/* --- platform-specific renderings (look-alikes, not pixel-perfect) --- */
function PlatformPost({ platform, text, images, activeIdx, onPrev, onNext }) {
    const active = images[activeIdx];
    const hasCarousel = images.length > 1;

    if (platform === "instagram") {
        return (
            <div className="max-w-md mx-auto bg-white text-black rounded-lg overflow-hidden shadow-2xl">
                <Header platform="instagram" />
                <div className="relative aspect-square bg-gray-100">
                    {active ? (
                        <img src={active.url} alt="publicación" className="w-full h-full object-cover" />
                    ) : (
                        <EmptyImage />
                    )}
                    {hasCarousel && <CarouselNav onPrev={onPrev} onNext={onNext} idx={activeIdx} total={images.length} />}
                </div>
                <ActionsRowIG />
                <div className="px-3 pb-3 text-sm">
                    <p className="font-semibold mb-1">tu_marca</p>
                    <p className="whitespace-pre-wrap leading-snug">{text}</p>
                </div>
            </div>
        );
    }

    if (platform === "facebook") {
        return (
            <div className="max-w-lg mx-auto bg-white text-black rounded-lg overflow-hidden shadow-2xl">
                <div className="p-3 flex items-center gap-2 border-b border-gray-200">
                    <div className="w-10 h-10 rounded-full bg-blue-500" />
                    <div>
                        <p className="font-semibold text-sm">Tu Marca</p>
                        <p className="text-xs text-gray-500">Hace 2 min · 🌐</p>
                    </div>
                </div>
                <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-snug">{text}</div>
                {/*
                 * Facebook mosaic — layout varies by image count to mirror
                 * the real feed:
                 *   1 img   → full 16:9
                 *   2 imgs  → side-by-side squares
                 *   3 imgs  → 1 tall left + 2 stacked right
                 *   4 imgs  → 1 wide top + 3 in a row below
                 *   5+      → 2 top + 3 bottom, last one shows "+N" overlay
                 *             when there are more than 5 images.
                 */}
                <FBMosaic images={images} />
                <ActionsRowFB />
            </div>
        );
    }

    // LinkedIn
    return (
        <div className="max-w-xl mx-auto bg-white text-black rounded-lg overflow-hidden shadow-2xl">
            <div className="p-3 flex items-center gap-2 border-b border-gray-200">
                <div className="w-12 h-12 rounded-full bg-blue-700" />
                <div>
                    <p className="font-semibold text-sm">Tu Marca</p>
                    <p className="text-xs text-gray-500">2,345 seguidores</p>
                    <p className="text-xs text-gray-500">Ahora · 🌐</p>
                </div>
            </div>
            <div className="px-3 py-3 text-sm whitespace-pre-wrap leading-relaxed">{text}</div>
            <div className="relative aspect-[1.91/1] bg-gray-100">
                {active ? (
                    <img src={active.url} alt="publicación" className="w-full h-full object-cover" />
                ) : (
                    <EmptyImage />
                )}
                {hasCarousel && <CarouselNav onPrev={onPrev} onNext={onNext} idx={activeIdx} total={images.length} />}
            </div>
            <ActionsRowLI />
        </div>
    );
}

function Header({ platform }) {
    return (
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-pink-500 via-orange-400 to-yellow-400 p-0.5">
                    <div className="w-full h-full rounded-full bg-white" />
                </div>
                <p className="font-semibold text-sm">tu_marca</p>
            </div>
            <span className="text-xl leading-none">⋯</span>
        </div>
    );
}

/**
 * Facebook-style mosaic. Adapts the layout to the number of images
 * (1..5+) to mirror what a real FB post looks like. Beyond 5 images
 * the 5th tile gets a translucent "+N" overlay.
 */
function FBMosaic({ images }) {
    if (!images || images.length === 0) {
        return (
            <div className="relative aspect-[16/9] bg-gray-100">
                <EmptyImage />
            </div>
        );
    }

    const Tile = ({ src, className = "", overlay = null }) => (
        <div className={`relative overflow-hidden bg-gray-100 ${className}`}>
            <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
            {overlay}
        </div>
    );

    if (images.length === 1) {
        return <Tile src={images[0].url} className="aspect-[16/9]" />;
    }
    if (images.length === 2) {
        return (
            <div className="grid grid-cols-2 gap-0.5">
                <Tile src={images[0].url} className="aspect-square" />
                <Tile src={images[1].url} className="aspect-square" />
            </div>
        );
    }
    if (images.length === 3) {
        return (
            <div
                className="grid grid-cols-2 grid-rows-2 gap-0.5"
                style={{ aspectRatio: "2 / 1" }}
            >
                <Tile src={images[0].url} className="row-span-2" />
                <Tile src={images[1].url} />
                <Tile src={images[2].url} />
            </div>
        );
    }
    if (images.length === 4) {
        return (
            <div
                className="grid grid-cols-3 grid-rows-2 gap-0.5"
                style={{ aspectRatio: "3 / 2" }}
            >
                <Tile src={images[0].url} className="col-span-3" />
                <Tile src={images[1].url} />
                <Tile src={images[2].url} />
                <Tile src={images[3].url} />
            </div>
        );
    }
    // 5+
    const extra = images.length - 5;
    return (
        <div
            className="grid grid-cols-6 grid-rows-2 gap-0.5"
            style={{ aspectRatio: "3 / 2" }}
        >
            <Tile src={images[0].url} className="col-span-3" />
            <Tile src={images[1].url} className="col-span-3" />
            <Tile src={images[2].url} className="col-span-2" />
            <Tile src={images[3].url} className="col-span-2" />
            <Tile
                src={images[4].url}
                className="col-span-2"
                overlay={
                    extra > 0 ? (
                        <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-semibold text-2xl">
                            +{extra}
                        </div>
                    ) : null
                }
            />
        </div>
    );
}

function CarouselNav({ onPrev, onNext, idx, total }) {
    return (
        <>
            {idx > 0 && (
                <button
                    onClick={onPrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 text-black flex items-center justify-center shadow"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
            )}
            {idx < total - 1 && (
                <button
                    onClick={onNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 text-black flex items-center justify-center shadow"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            )}
            <div className="absolute bottom-2 right-2 bg-black/55 text-white text-[11px] px-2 py-0.5 rounded-full font-medium">
                {idx + 1}/{total}
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {Array.from({ length: total }).map((_, i) => (
                    <span
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/50"}`}
                    />
                ))}
            </div>
        </>
    );
}

function EmptyImage() {
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 text-sm">
            <ImageIcon className="w-10 h-10 mb-2" />
            Sin imagen — usa "Traer desde referencias" o "Subir"
        </div>
    );
}

function ActionsRowIG() {
    return (
        <div className="px-3 py-2 flex items-center gap-3 text-2xl">
            <span>♡</span>
            <span>💬</span>
            <span>↗</span>
            <span className="ml-auto">🔖</span>
        </div>
    );
}

function ActionsRowFB() {
    return (
        <div className="grid grid-cols-3 border-t border-gray-200 text-gray-600 text-sm">
            <button className="py-2 hover:bg-gray-50">👍 Me gusta</button>
            <button className="py-2 hover:bg-gray-50">💬 Comentar</button>
            <button className="py-2 hover:bg-gray-50">↗ Compartir</button>
        </div>
    );
}

function ActionsRowLI() {
    return (
        <div className="grid grid-cols-4 border-t border-gray-200 text-gray-600 text-xs">
            <button className="py-2.5 hover:bg-gray-50">👍 Recomendar</button>
            <button className="py-2.5 hover:bg-gray-50">💬 Comentar</button>
            <button className="py-2.5 hover:bg-gray-50">↗ Compartir</button>
            <button className="py-2.5 hover:bg-gray-50">📤 Enviar</button>
        </div>
    );
}
